import { useCallback, useState } from 'react';

// Data Layer
import { coverageListWithout } from 'data-layer-cr/dist/functions/quotations';
import { CoverageLimit } from 'data-layer-cr/dist/models/CoverageCatalog';
import QuotationCoverage, {
  makeEmptyQuotationCoverage,
} from 'data-layer-cr/dist/models/QuotationCoverage';
import { ExemptionCoverageId } from '../../components/IndividualCoverages/ExemptionCoverage';

/**
 * The initial value for the endorsement reference. We use a "mock" empty endorsement to
 * avoid dealing with nullable values.
 */
export const emptyEndorsement: QuotationCoverage = makeEmptyQuotationCoverage();

/**
 * A helper method that creates a simplified coverage with the provided data.
 * @param code - The code to use as "coverageId"
 * @param value - The value to register as "insured value"
 */
function makeSimplifiedCoverage(
  code: string,
  value: number,
): QuotationCoverage {
  return {
    ...makeEmptyQuotationCoverage(),
    coverageId: code,
    insuredValue: value,
    deductible: 0,
    deductibleRate: 0,
    deductibleDescription: 'M',
  };
}
export function makeExemptionCoverage(): QuotationCoverage {
  return {
    ...makeEmptyQuotationCoverage(),
    coverageId: ExemptionCoverageId,
    insuredValue: 0,
    deductible: 0,
    deductibleRate: 0,
    deductibleDescription: 'D',
  };
}

/**
 * Coverage management inside `DynamicQuotationPage` was getting a bit out of hand, so I
 * decided to extract all the logic related to coverage transactions inside this hook. It
 * makes it so that all transactions are predictable, and all callback methods are called
 * when needed (this is important, as including this logic inside DynamicQuotationPage
 * directly was causing that component declaration to exceed the recommended max method
 * size, and was causing an infinite render loop error due to how the lifecycle was treated).
 *
 * @param selectedProduct - The selected product type
 * @param onCoveragesUpdated - Called when an update transaction is requested
 */
function useCoverageTracker(
  currentDeductibleOption: number,
  includeAllOptionsInitialVal: boolean,
  onCoveragesUpdated: (
    newCoverages: QuotationCoverage[],
    deductibleOption: number,
  ) => void,
  onEndorsementChange: (
    modifiedPackage: number,
    newCoverages: QuotationCoverage,
    deductibleOption: number,
  ) => void,
  onCustomerProfileChange: (
    modifiedPackage: number,
    newCoverages: QuotationCoverage,
    deductibleOption: number,
    packageSelected: string,
  ) => void,
  onChangeEndorsementExemption: (
    modifiedPackage: number,
    newCoverages: QuotationCoverage,
    deductibleOption: number,
    addExemptionCoverage: boolean,
    packageSelected: string,
  ) => void,
): {
  selectedCoverages: QuotationCoverage[];
  selectedEndorsement: QuotationCoverage;
  selectedDeductibleOption: number;
  addNewCoverage: (code: string, limit: CoverageLimit) => void;
  updateEndorsement: (
    modifiedPackage: number,
    code: string,
    value: number,
  ) => void;
  updateCustomerProfile: (
    modifiedPackage: number,
    code: string,
    value: number,
    packageSelected: string,
  ) => void;
  updateEndorsementExemption: (
    modifiedPackage: number,
    addExemptionCoverage: boolean,
    packageSelected: string,
  ) => void;
  updateDeductibleOption: (newOption: number) => void;
  overwriteAll: (
    simplifiedCoverages: QuotationCoverage[],
    endorsement: QuotationCoverage | undefined,
  ) => void;
  includeAllOptions: boolean;
  toggleIncludeAllOptions: (checked: boolean) => void;
} {
  const [selectedCoverages, updateSelectedCoverages] = useState<
    QuotationCoverage[]
  >([]);
  const [selectedEndorsement, updateSelectedEndorsement] =
    useState(emptyEndorsement);
  const [selectedDeductibleOption, updateSelectedDeductibleOption] = useState(
    currentDeductibleOption,
  );
  const [includeAllOptions, toggleIncludeAllOptions] = useState(
    includeAllOptionsInitialVal,
  );

  /**
   * Registers the provided coverage code and limit to the selected coverages reference.
   * Note that `simplifiedCoverageListWithout` will ensure no duplicate values are added.
   *
   * Notifies `onCoveragesUpdated` with new values
   *
   * @param code - The new coverage's code.
   * @param limit - The selected limit for the new coverage.
   */
  const addNewCoverage = useCallback(
    (code: string, limit: CoverageLimit): void => {
      const newSelectedCoverages = coverageListWithout(selectedCoverages, code);
      newSelectedCoverages.push(makeSimplifiedCoverage(code, limit.maximum));

      const combinedLists = [selectedEndorsement, ...newSelectedCoverages];
      onCoveragesUpdated(combinedLists, selectedDeductibleOption);

      updateSelectedCoverages(newSelectedCoverages);
    },
    [
      selectedCoverages,
      selectedDeductibleOption,
      selectedEndorsement,
      onCoveragesUpdated,
    ],
  );

  /**
   * Registers the provided parameters as the selected endorsement option. Since there
   * can only be one endorsement, this logic is separate from regular coverages as all
   * transactions must overwrite the previous value.
   *
   * Notifies `onCoveragesUpdated` with new values
   *
   * @param code - The new endorsement's code
   * @param value - The new endorsement's value
   */
  const updateEndorsement = useCallback(
    (modifiedPackage: number, code: string, value: number): void => {
      const newEndorsement = makeSimplifiedCoverage(code, value);
      onEndorsementChange(
        modifiedPackage,
        newEndorsement,
        selectedDeductibleOption,
      );

      updateSelectedEndorsement(newEndorsement);
    },
    [selectedDeductibleOption, onEndorsementChange],
  );

  const updateCustomerProfile = useCallback(
    (
      modifiedPackage: number,
      code: string,
      value: number,
      packageSelected: string,
    ): void => {
      const newEndorsement = makeSimplifiedCoverage(code, value);
      onCustomerProfileChange(
        modifiedPackage,
        newEndorsement,
        selectedDeductibleOption,
        packageSelected,
      );

      updateSelectedEndorsement(newEndorsement);
    },
    [selectedDeductibleOption, onCustomerProfileChange],
  );

  const updateEndorsementExemption = useCallback(
    (
      modifiedPackage: number,
      addExemptionCoverage: boolean,
      packageSelected: string,
    ): void => {
      const newEndorsement = makeExemptionCoverage();
      onChangeEndorsementExemption(
        modifiedPackage,
        newEndorsement,
        selectedDeductibleOption,
        addExemptionCoverage,
        packageSelected,
      );

      updateSelectedEndorsement(newEndorsement);
    },
    [selectedDeductibleOption, onChangeEndorsementExemption],
  );

  /**
   * Changes the current deductible option to the new value, and triggers the
   * `onCoveragesUpdated` callback.
   *
   * @param newOption - The new deductible option (from the slider)
   */
  const updateDeductibleOption = useCallback(
    (newOption: number): void => {
      const combinedLists = [selectedEndorsement, ...selectedCoverages];
      onCoveragesUpdated(combinedLists, newOption);
      updateSelectedDeductibleOption(newOption);
    },
    [selectedEndorsement, selectedCoverages, onCoveragesUpdated],
  );

  /**
   * Overwrites all internal coverage values with the provided data. Note that this method
   * will not trigger `onCoverageUpdated` as it is considered a cleanup method and calling
   * it could result in an infinite render loop (see DynamicQuotationPage's implementation).
   *
   * @param simplifiedCoverages - The new list for the selectedCoverage reference.
   * @param endorsement - The new reference to use as the selectedEndorsement.
   */
  const overwriteAll = useCallback(
    (
      simplifiedCoverages: QuotationCoverage[],
      endorsement?: QuotationCoverage | undefined,
    ): void => {
      updateSelectedCoverages(simplifiedCoverages);
      if (typeof endorsement !== 'undefined') {
        updateSelectedEndorsement(endorsement);
      } else {
        updateSelectedEndorsement(emptyEndorsement);
      }
    },
    [],
  );

  return {
    selectedCoverages,
    selectedEndorsement,
    selectedDeductibleOption,
    addNewCoverage,
    updateEndorsement,
    updateDeductibleOption,
    overwriteAll,
    includeAllOptions,
    toggleIncludeAllOptions,
    updateEndorsementExemption,
    updateCustomerProfile,
  };
}

export default useCoverageTracker;
